import axios from 'axios';
import { WEB_API_BASE_URL } from '../config/config';
import imageUploadService from './imageUploadService';
import debugService from './debugService';
import { handleSessionExpired } from '../utils/sessionExpiredHandler';

/**
 * Web API Form Service
 * Integrates with the main Coca-Cola Web API for dynamic form submission
 * Uses the DynamicForm/SaveDynamicFormData endpoint
 */
class WebApiFormService {
  private webApiClient = axios.create({
    baseURL: WEB_API_BASE_URL,
    timeout: 60000, // Increased timeout for form submissions with images
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // Add auth token interceptor
    this.webApiClient.interceptors.request.use(
      (config) => {
        // Use the same token from CloudFlare auth
        const token = localStorage.getItem('authToken');

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for better error handling
    this.webApiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleSessionExpired();
        }
        return Promise.reject(error);
      }
    );

    // Add debug interceptors
    debugService.addInterceptors(this.webApiClient, 'WebAPI');
  }


  /**
   * Get form history for revert functionality
   */
  async getFormHistory(uid: string) {
    try {
      const url = `/ApprovalMatrix/GetFormHistory?uid=${encodeURIComponent(uid)}`;
      
      // Check if auth token exists
      const token = localStorage.getItem('authToken');
      
      // Make request with explicit Authorization header
      const response = await this.webApiClient.get(url, {
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });

      // Try multiple possible response structures
      let historyArray: any[] = [];
      
      // 1. Check for FormHistory inside Data
      if (response.data?.Data?.FormHistory && Array.isArray(response.data.Data.FormHistory)) {
        historyArray = response.data.Data.FormHistory;
      }
      // 2. Check if Data itself is the array
      else if (response.data?.Data && Array.isArray(response.data.Data)) {
        historyArray = response.data.Data;
      }
      // 3. Check for GetFormHistoryResult
      else if (response.data?.Data?.GetFormHistoryResult && Array.isArray(response.data.Data.GetFormHistoryResult)) {
        historyArray = response.data.Data.GetFormHistoryResult;
      }
      // 4. Check all properties of Data for an array
      else if (response.data?.Data) {
        const dataKeys = Object.keys(response.data.Data);
        for (const key of dataKeys) {
          if (Array.isArray(response.data.Data[key])) {
            historyArray = response.data.Data[key];
            break;
          }
        }
      }

      if (historyArray.length > 0) {
        return {
          success: true,
          data: historyArray,
          message: 'Form history loaded successfully'
        };
      }
      
      return {
        success: false,
        data: [],
        message: 'No form history found'
      };
    } catch (error: any) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.Message || error.message || 'Failed to load form history'
      };
    }
  }

  /**
   * Get existing form data for edit mode
   */
  async getFormData(formName: string, uid: string) {
    try {
      const url = `/DynamicForm/GetDynamicFormData/${encodeURIComponent(formName)}/${encodeURIComponent(uid)}`;
      
      const response = await this.webApiClient.get(url);
      
      if (response.data?.Data?.FormData) {
        return {
          success: true,
          data: response.data.Data.FormData,
          message: 'Form data loaded successfully'
        };
      }
      
      return {
        success: false,
        data: {},
        message: 'No form data found'
      };
    } catch (error: any) {
      return {
        success: false,
        data: {},
        message: error.message || 'Failed to load form data'
      };
    }
  }

  /**
   * Submit dynamic form data to Web API
   * This is EXACTLY how the main UI does it
   */
  async submitDynamicForm(
    formName: string,
    formData: any,
    options?: {
      nextStatus?: string;
      approvalMatrixLineUID?: string;
      isReverted?: boolean;
      remarks?: string;
      selectedRevertHistoryUID?: string;
      referenceType?: string;
      referenceUID?: string;
      uid?: string;
      mode?: 'ADD' | 'EDIT' | 'VIEW';
      isPublicForm?: boolean;
    }
  ) {
    try {
      // Build query parameters exactly like main UI
      const queryParams = new URLSearchParams();
      if (options?.nextStatus) queryParams.append('NextStatus', options.nextStatus);
      // CRITICAL: Always append ApprovalMatrixLineUID, even if empty, to prevent SQL error
      if (options?.approvalMatrixLineUID !== undefined) queryParams.append('ApprovalMatrixLineUID', options.approvalMatrixLineUID);
      if (options?.isReverted !== undefined) queryParams.append('IsReverted', options.isReverted ? '1' : '0');
      if (options?.remarks) queryParams.append('Remarks', options.remarks);
      if (options?.selectedRevertHistoryUID) queryParams.append('SelectedRevertHistoryUID', options.selectedRevertHistoryUID);

      const queryString = queryParams.toString();

      // Use PublicDynamicForm endpoint for public forms (Customer Unit Registration and Verification)
      const endpoint = options?.isPublicForm ? 'PublicDynamicForm' : 'DynamicForm';
      const url = `/${endpoint}/SaveDynamicFormData/${encodeURIComponent(formName)}${queryString ? '?' + queryString : ''}`;

      // CRITICAL FIX: Handle UID for EDIT mode - backend expects uppercase UID
      // But keep lowercase 'uid' in the payload to match Angular
      if (options?.uid && options?.mode === 'EDIT') {
        const headerTableName = Object.keys(formData).find(tableName => 
          !Array.isArray(formData[tableName]) && typeof formData[tableName] === 'object'
        );
        
        if (headerTableName && formData[headerTableName]) {
          // Ensure UID is set with the correct value (keep as lowercase 'uid' to match Angular)
          if (!formData[headerTableName].uid) {
            formData[headerTableName].uid = options.uid;
          }
        }
      }

      // CRITICAL: Update status column in ADD mode OR when reverting (align with Angular behavior)
      // In normal EDIT mode (non-revert), keep the current status from loaded data
      // But for REVERT operations, we need to update the status even in EDIT mode
      const shouldUpdateStatus = options?.nextStatus && (options?.mode !== 'EDIT' || options?.isReverted);

      if (shouldUpdateStatus) {
        const applyStatusToRow = (row: Record<string, any>) => {
          if (!row || typeof row !== 'object') {
            return;
          }

          const keys = Object.keys(row);
          const directMatch = keys.find(key => key.toLowerCase() === 'status');
          const derivedMatch = directMatch || keys.find(key => key.toLowerCase().endsWith('_status'));

          if (derivedMatch) {
            row[derivedMatch] = options.nextStatus;
          }
        };

        Object.keys(formData).forEach(tableName => {
          const tableData = formData[tableName];

          if (!tableData) {
            return;
          }

          if (Array.isArray(tableData)) {
            tableData.forEach(applyStatusToRow);
          } else if (typeof tableData === 'object') {
            applyStatusToRow(tableData);
          }
        });
      }

      // Add reference information to form data if provided
      if (options?.referenceType && options?.referenceUID) {
        // Add to header table if it exists
        const headerTableName = Object.keys(formData).find(key => key.includes('header'));
        if (headerTableName && formData[headerTableName]) {
          formData[headerTableName].ReferenceType = options.referenceType;
          formData[headerTableName].ReferenceUID = options.referenceUID;
        }
      }

      // Submit directly to Web API
      const response = await this.webApiClient.post(url, formData);



      console.log('Web API Response:', response.data);

      // Log SavedUIDs to see what was actually saved
      if (response.data?.Data?.SavedUIDs) {
        const savedUIDs = response.data.Data.SavedUIDs;
        console.log('SavedUIDs:', savedUIDs);
        const savedKeys = Object.keys(savedUIDs);
        if (savedKeys.length === 0) {
          console.warn('WARNING: SavedUIDs is empty - no records were actually saved in database!');
        } else {
          console.log('Records saved in tables:', savedKeys);
          savedKeys.forEach(table => {
            console.log(`${table}: ${savedUIDs[table]}`);
          });
        }
      } else {
        console.warn('No SavedUIDs in response - data may not have been saved');
      }

      // Handle response based on the AResponse structure
      if (response.data?.Status?.Type === 1) {
        return {
          success: true,
          message: response.data?.Status?.Msg || 'Form submitted successfully',
          data: response.data?.Data,
          referenceNumber: response.data?.Data?.ReferenceNumber
        };
      } else {
        return {
          success: false,
          message: response.data?.Status?.Msg || 'Form submission failed',
          errors: response.data?.Errors
        };
      }
    } catch (error: any) {
      console.error('Error submitting form to Web API:', error);
      return {
        success: false,
        message: error.response?.data?.Status?.Msg ||
                 error.response?.data?.message ||
                 error.message ||
                 'Failed to submit form',
        errors: error.response?.data?.Errors
      };
    }
  }

  /**
   * Convert File to base64 string
   */
  private convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Prepare form data for submission
   * Organizes data into correct tables based on Table_Name from API configuration
   */
  prepareFormData(
    formType: string,
    formFields: any,
    imageFields?: { [fieldName: string]: string[] },
    tableNames?: { header?: string; detail?: string },
    fieldConfigs?: any[]
  ): any {
    const formData: any = {};

    // Build field-to-table mapping ONLY for visible fields
    const fieldTableMap: { [fieldName: string]: string } = {};
    const fieldTypeMap: { [fieldName: string]: string } = {};
    const tableTypeByName: { [tableName: string]: string } = {};
    const repeatingTableNames: Set<string> = new Set();
    const availableTables: Set<string> = new Set();

    const columnAliasMap: Map<string, string> = new Map();
    const registerColumnAlias = (tableName: string, alias: string | null | undefined, resolved: string | null | undefined) => {
      const normalizedTable = normalizeTableName(tableName);
      const resolvedSanitized = sanitizeColumnCandidate(resolved);

      if (!normalizedTable || !resolvedSanitized) {
        return;
      }

      const aliasCandidates: (string | null)[] = [];

      const aliasSanitized = sanitizeColumnCandidate(alias);
      if (typeof alias === 'string') {
        const trimmed = alias.trim();
        if (trimmed) {
          aliasCandidates.push(trimmed, trimmed.toLowerCase());
        }
      }

      if (aliasSanitized) {
        aliasCandidates.push(aliasSanitized, aliasSanitized.toLowerCase());
      }

      aliasCandidates.forEach(candidate => {
        if (!candidate) {
          return;
        }
        const key = `${normalizedTable}::${candidate}`;
        if (!columnAliasMap.has(key)) {
          columnAliasMap.set(key, resolvedSanitized);
        }
      });
    };

    const shouldIgnoreFieldForMapping = (field: any, resolvedColumnName: string | null): boolean => {
      if (!resolvedColumnName) {
        return false;
      }

      // CRITICAL: Only ignore paragraph/display-only fields, NOT data fields
      // Paragraph fields are for display purposes only and don't need database columns
      const inputType = (field?.UI_Input_Type || field?.inputType || '').toLowerCase();
      const isParagraphField = inputType === 'p' || inputType === 'paragraph' || inputType === 'h2';

      // If it's a paragraph/heading field, ignore it for data mapping
      if (isParagraphField) {
        return true;
      }

      const rawColumnCandidate = field?.Table_Column_Name || field?.tableColumnName || field?.columnName;
      if (typeof rawColumnCandidate !== 'string') {
        return false;
      }

      // FIXED: Don't ignore fields just because column name has spaces
      // Only ignore if column name is COMPLETELY invalid (empty after sanitization)
      if (!/[^a-zA-Z0-9_]/.test(rawColumnCandidate)) {
        return false;
      }

      const sanitizedResolved = sanitizeColumnCandidate(resolvedColumnName);
      const sanitizedUiText = sanitizeColumnCandidate(field?.UI_Field_Text || field?.fieldText);

      // CRITICAL FIX: Don't ignore data fields that have valid column names
      // Only ignore if the column name appears to be UI text (no valid column found)
      // AND the field is not a data entry field
      if (sanitizedResolved && sanitizedUiText && sanitizedResolved === sanitizedUiText) {
        // Check if this is a data entry field (has proper column type)
        const hasValidColumnType = field?.Table_Column_Type && field.Table_Column_Type !== '';

        // If it has a valid column type, it's a real data field - DON'T ignore it
        if (hasValidColumnType) {
          return false;
        }

        // Otherwise, it's probably a display field with UI text as column name - ignore it
        return true;
      }

      return false;
    };

    const slugify = (value: string): string => (
      value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_+|_+$/g, '')
    );

    const sanitizeColumnCandidate = (candidate: string | null | undefined): string | null => {
      if (typeof candidate !== 'string') {
        return null;
      }

      const trimmed = candidate.trim();
      if (!trimmed) {
        return null;
      }

      if (/[^a-zA-Z0-9_]/.test(trimmed)) {
        const slug = slugify(trimmed);
        return slug || null;
      }

      return trimmed;
    };

    const resolveColumnName = (field: any): string | null => {
      const metadataCandidates = [
        field?.tableColumnNameLower,
        field?.Table_Column_Name_Lower,
        field?.Table_Column_Name_LowerCase,
        field?.tablecolumnnamelower,
        field?.tableColumnName,
        field?.Table_Column_Name,
        field?.columnName
      ];

      for (const candidate of metadataCandidates) {
        const sanitized = sanitizeColumnCandidate(candidate);
        if (sanitized) {
          return sanitized;
        }
      }

      const fallbackCandidates = [
        field?.UI_Field_Id,
        field?.UiFieldId,
        field?.fieldId,
        field?.fieldName,
        field?.UI_Field_Text,
        field?.fieldText
      ];

      for (const candidate of fallbackCandidates) {
        const sanitized = sanitizeColumnCandidate(candidate);
        if (sanitized) {
          return sanitized;
        }
      }

      return null;
    };

    const normalizeTableName = (name: string | null | undefined): string | null => {
      if (!name) {
        return null;
      }
      const trimmed = name.trim();
      return trimmed ? trimmed.toLowerCase() : null;
    };

    const allowedColumnsByTable: Record<string, Set<string>> = {};

    const registerAllowedColumn = (tableName: string, columnName: string | null | undefined) => {
      const normalizedTable = normalizeTableName(tableName);
      if (!normalizedTable || !columnName) {
        return;
      }

      const trimmed = columnName.trim();
      if (!trimmed) {
        return;
      }

      const lower = trimmed.toLowerCase();
      if (!allowedColumnsByTable[normalizedTable]) {
        allowedColumnsByTable[normalizedTable] = new Set();
      }
      allowedColumnsByTable[normalizedTable].add(lower);
    };

    const filterRowColumns = (tableName: string, row: Record<string, any>): Record<string, any> => {
      if (!row || typeof row !== 'object') {
        return row;
      }

      const normalizedTable = normalizeTableName(tableName);
      const allowed = normalizedTable ? allowedColumnsByTable[normalizedTable] : undefined;
      if (!allowed || allowed.size === 0) {
        return row;
      }

      const filtered: Record<string, any> = {};
      Object.keys(row).forEach(key => {
        const lowerKey = key.toLowerCase();
        if (allowed.has(lowerKey)) {
          // CRITICAL: Force uid field to lowercase to match Angular
          const finalKey = lowerKey === 'uid' ? 'uid' : key;
          filtered[finalKey] = row[key];
        } else if (normalizedTable) {
          const aliasKey = `${normalizedTable}::${key}`;
          const aliasKeyLower = `${normalizedTable}::${lowerKey}`;
          const resolved = columnAliasMap.get(aliasKey) || columnAliasMap.get(aliasKeyLower);
          if (resolved && allowed.has(resolved.toLowerCase())) {
            // CRITICAL: Force uid field to lowercase to match Angular
            const finalResolved = resolved.toLowerCase() === 'uid' ? 'uid' : resolved;
            filtered[finalResolved] = row[key];
          }
        }
      });

      return filtered;
    };

    if (fieldConfigs) {
      fieldConfigs.forEach(field => {
        const tableColumnNameRaw = field.Table_Column_Name || field.tableColumnName || field.columnName || '';
        const tableName = field.Table_Name || field.tableName || '';
        const columnType = field.Table_Column_Type || field.columnType || '';
        const tableType = (field.Table_Type || field.tableType || '').toLowerCase();
        const repeatingSectionId = field.Repeating_Section_Id || field.repeatingSectionId;

        if (tableName && tableName.trim() !== '') {
          const resolvedColumnName = resolveColumnName(field);
          const ignoreField = shouldIgnoreFieldForMapping(field, resolvedColumnName);

          const rawColumnCandidates = [tableColumnNameRaw].filter(candidate => typeof candidate === 'string' && candidate.trim());
          rawColumnCandidates.forEach(candidate => {
            fieldTableMap[candidate] = tableName;
            fieldTableMap[candidate.toLowerCase()] = tableName;
            if (!ignoreField) {
              registerColumnAlias(tableName, candidate, resolvedColumnName || candidate);
            }
          });

          if (ignoreField) {
            return;
          }

          if (resolvedColumnName) {
            fieldTableMap[resolvedColumnName] = tableName;
            fieldTableMap[resolvedColumnName.toLowerCase()] = tableName;
            registerAllowedColumn(tableName, resolvedColumnName);
            fieldTypeMap[resolvedColumnName] = columnType;
          }

          const uiFieldId = field.UI_Field_Id || field.UiFieldId || field.fieldId || field.fieldName;
          if (uiFieldId) {
            fieldTableMap[uiFieldId] = tableName;
            fieldTableMap[uiFieldId.toLowerCase()] = tableName;
            if (!ignoreField) {
              registerColumnAlias(tableName, uiFieldId, resolvedColumnName);
            }
          }

          const uiFieldText = field.UI_Field_Text || field.fieldText || field.label;
          if (uiFieldText) {
            if (!ignoreField) {
              registerColumnAlias(tableName, uiFieldText, resolvedColumnName);
            }
          }

          availableTables.add(tableName);
          if (tableType && !tableTypeByName[tableName]) {
            tableTypeByName[tableName] = tableType;
          }
          // CRITICAL FIX: Only mark a table as repeating if it's actually a detail table
          // Don't mark header tables as repeating just because they have repeating section fields
          if (repeatingSectionId && tableName && tableType && tableType.toLowerCase() !== 'header') {
            repeatingTableNames.add(tableName);
          }
          // console.log(`Field ${tableColumnName} belongs to table ${tableName} with type ${columnType} (visible: ${isVisible})`);
        }
      });
    }

    // Initialize table data objects and metadata maps
    const tableData: { [tableName: string]: any } = {};
    const fieldsByTable: Record<string, any[]> = {};
    const repeatingSectionConfigs: Record<string, { tableName: string; fields: any[]; parentSectionId?: string }> = {};
    const preferredHeaderTableName = tableNames?.header || '';

    availableTables.forEach(tableName => {
      if (repeatingTableNames.has(tableName) || (tableTypeByName[tableName] && tableTypeByName[tableName] !== 'header')) {
        tableData[tableName] = [];
      } else {
        tableData[tableName] = {};
      }
    });

    if (fieldConfigs) {
      fieldConfigs.forEach(field => {
        const tableName = field.Table_Name || field.tableName || '';
        const repeatingSectionId = field.Repeating_Section_Id || field.repeatingSectionId;

        if (tableName) {
          if (!fieldsByTable[tableName]) {
            fieldsByTable[tableName] = [];
          }
          fieldsByTable[tableName].push(field);
        }

        if (repeatingSectionId) {
          if (!repeatingSectionConfigs[repeatingSectionId]) {
            repeatingSectionConfigs[repeatingSectionId] = {
              tableName,
              fields: [],
              parentSectionId: field.Repeating_Parent_Section_Id || field.repeatingParentSectionId
            };
          }
          repeatingSectionConfigs[repeatingSectionId].fields.push(field);
        }
      });
    }

    const repeatingSectionData = formFields?._repeatingSectionData || {};
    const flatFieldValues: Record<string, any> = {};
    Object.entries(formFields || {}).forEach(([key, value]) => {
      if (key !== '_repeatingSectionData') {
        flatFieldValues[key] = value;
        flatFieldValues[key.toLowerCase()] = value;
      }
    });

    // Generate UUID v4 function (same as Guid.NewGuid() in C#)
    const generateUID = (): string => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    };

    const formatDateTimeForSql = (rawValue: any): string | null => {
      if (rawValue === undefined || rawValue === null || rawValue === '') {
        return null;
      }

      const date = rawValue instanceof Date ? rawValue : new Date(rawValue);

      if (Number.isNaN(date.getTime())) {
        // If the value is already a string, return as-is (backend may handle custom formats)
        return typeof rawValue === 'string' ? rawValue : null;
      }

      const pad = (value: number) => value.toString().padStart(2, '0');

      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const formatDateForSql = (rawValue: any): string | null => {
      if (rawValue === undefined || rawValue === null || rawValue === '') {
        return null;
      }

      const date = rawValue instanceof Date ? rawValue : new Date(rawValue);

      if (Number.isNaN(date.getTime())) {
        return typeof rawValue === 'string' ? rawValue : null;
      }

      const pad = (value: number) => value.toString().padStart(2, '0');

      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());

      return `${year}-${month}-${day}`;
    };

    const transformValueForSubmission = (field: any, rawValue: any): any => {
      if (rawValue === undefined) {
        return undefined;
      }

      if (rawValue === null) {
        return null;
      }

      const columnName = (field?.Table_Column_Name || field?.tableColumnName || field?.columnName || field?.fieldName || '').toString();
      const columnNameLower = columnName.toLowerCase();
      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        if (trimmed.toLowerCase() === 'null') {
          return null;
        }
        if (trimmed === '' && (field?.fieldType === 'number' || (field?.Table_Column_Type || '').toLowerCase().includes('int'))) {
          return null;
        }
      }

      const columnType = (field?.Table_Column_Type || field?.tableColumnType || field?.columnType || '').toLowerCase();
      const inputType = (field?.inputType || field?.InputType || '').toLowerCase();

      const normalizePlaceholderValue = (value: any): any => {
        if (typeof value !== 'string') {
          return value;
        }

        const lowered = value.trim().toLowerCase();

        if (lowered === 'newid') {
          return generateUID();
        }

        if (lowered === 'currentdatetime') {
          return new Date();
        }

        if (lowered === 'currentdate') {
          return new Date();
        }

        if (lowered === 'backend') {
          return null;
        }

        return value;
      };

      if (columnType.includes('datetime')) {
        const normalized = normalizePlaceholderValue(rawValue);
        return formatDateTimeForSql(normalized);
      }

      if (columnType === 'date' || (columnType.includes('date') && !columnType.includes('time'))) {
        const normalized = normalizePlaceholderValue(rawValue);
        return formatDateForSql(normalized);
      }

      if (columnType.includes('int') || columnType.includes('decimal') || columnType.includes('numeric') || columnType.includes('float')) {
        if (rawValue === '') {
          return null;
        }
        const num = Number(rawValue);
        return Number.isNaN(num) ? null : num;
      }

      if (columnType.includes('bit') || inputType === 'checkbox') {
        if (rawValue === '' || rawValue === null) {
          return 0;
        }

        if (typeof rawValue === 'boolean') {
          return rawValue ? 1 : 0;
        }

        if (typeof rawValue === 'string') {
          const normalized = normalizePlaceholderValue(rawValue);
          if (typeof normalized !== 'string') {
            return normalized;
          }

          const lower = normalized.toLowerCase();
          if (lower === 'true' || lower === '1' || lower === 'yes') {
            return 1;
          }
          if (lower === 'false' || lower === '0' || lower === 'no') {
            return 0;
          }
        }

        return rawValue ? 1 : 0;
      }

      if (rawValue instanceof Date) {
        return rawValue.toISOString();
      }

      const normalized = normalizePlaceholderValue(rawValue);
      return normalized instanceof Date ? formatDateTimeForSql(normalized) : normalized;
    };

    const sqlDataTypes = ['nvarchar', 'varchar', 'int', 'decimal', 'datetime', 'bit', 'float', 'char'];

    const parseSystemDefaultValue = (field: any): any => {
      const raw = field?.inputValue ?? field?.InputValue ?? field?.fieldInputValue ?? field?.defaultValue ?? field?.DefaultValue ?? field?.Field_Default_Value;


      if (raw === undefined || raw === null) {
        return null;
      }

      if (typeof raw === 'number') {
        return raw;
      }

      const rawString = String(raw).trim();
      if (!rawString) {
        return null;
      }

      const lower = rawString.toLowerCase();

      if (lower === '0') {
        return 0;
      }

      if (lower === 'null' || lower === 'backend') {
        return null;
      }

      if (lower === 'newid') {
        return generateUID();
      }

      if (lower === 'currentdatetime') {
        return new Date().toISOString();
      }

      if (lower === 'parent') {
        return null;
      }

      if (!isNaN(Number(rawString))) {
        return Number(rawString);
      }

      return raw;
    };

    const sectionConfigParentField = (tableName: string, systemFieldsByTableMap: Record<string, any[]>): string | null => {
      const systemFields = systemFieldsByTableMap[tableName] || [];

      const parentField = systemFields.find(field => {
        const raw = field?.inputValue ?? field?.InputValue ?? field?.fieldInputValue ?? field?.defaultValue ?? field?.DefaultValue ?? field?.Field_Default_Value;
        return typeof raw === 'string' && raw.toLowerCase() === 'parent';
      });

      if (!parentField) {
        return null;
      }

      return parentField.tableColumnName || parentField.Table_Column_Name || null;
    };

    // Populate non-repeating tables using metadata
    Object.entries(fieldsByTable).forEach(([tableName, fields]) => {
      if (Array.isArray(tableData[tableName])) {
        return;
      }

      const targetRow = tableData[tableName];
      let populatedCount = 0;

      fields.forEach(field => {
        const columnName = resolveColumnName(field);
        if (!columnName) {
          return;
        }

        if (shouldIgnoreFieldForMapping(field, columnName)) {
          return;
        }

        const repeatingSectionId = field.Repeating_Section_Id || field.repeatingSectionId;
        if (repeatingSectionId) {
          return; // handled separately
        }

        const columnNameLower = columnName.toLowerCase();
        if (sqlDataTypes.some(dt => columnNameLower.includes(dt) && columnNameLower.includes('('))) {
          return;
        }

        const isSystemField = ((field.inputType || field.InputType || '').toLowerCase() === 'system');

        const candidateKeys = [
          field.fieldName,
          field.UI_Field_Id,
          columnName
        ].filter(Boolean) as string[];

        let value: any = undefined;

        for (const key of candidateKeys) {
          if (key && flatFieldValues[key] !== undefined) {
            value = flatFieldValues[key];
            populatedCount++;
            break;
          }
          if (key && flatFieldValues[key.toLowerCase()] !== undefined) {
            value = flatFieldValues[key.toLowerCase()];
            populatedCount++;
            break;
          }
        }

        if (imageFields) {
          const imageCandidates = [
            columnName,
            columnName.toLowerCase(),
            field.UI_Field_Id,
            field.UI_Field_Id?.toLowerCase(),
            field.fieldName,
            field.fieldName?.toLowerCase()
          ].filter(Boolean) as string[];

          for (const candidate of imageCandidates) {
            if (imageFields[candidate]) {
              value = imageFields[candidate].join('|');
              break;
            }
          }
        }

        value = transformValueForSubmission(field, value);

        // CRITICAL: Don't regenerate UID for system fields if value already exists
        // This prevents creating new UIDs in EDIT mode
        if ((value === undefined || value === null || value === '') && isSystemField) {
          // Special handling for UID field - don't regenerate if it exists in the input data
          const isUidField = columnName && columnName.toLowerCase() === 'uid';
          if (isUidField) {
            // Check if UID exists in the input form fields (case-insensitive)
            const existingUid = flatFieldValues['uid'] || flatFieldValues['UID'] ||
                               flatFieldValues[columnName] || flatFieldValues[columnName.toLowerCase()];
            if (existingUid) {
              // UID already exists in input data, use it
              value = existingUid;
            } else {
              // No UID in input, generate new one (ADD mode)
              value = transformValueForSubmission(field, parseSystemDefaultValue(field));
            }
          } else {
            value = transformValueForSubmission(field, parseSystemDefaultValue(field));
          }
        }

        if (value === undefined) {
          const defaultValue = field.defaultValue ?? field.DefaultValue ?? field.Field_Default_Value;
          if (defaultValue !== undefined) {
            value = transformValueForSubmission(field, defaultValue);
          }
        }

        if (value === undefined) {
          value = null;
        }

        targetRow[columnName] = value;
      });
    });

    // Populate repeating tables based on repeating section metadata
    Object.entries(repeatingSectionData as Record<string, any[]>).forEach(([sectionId, rows]) => {
      const sectionConfig = repeatingSectionConfigs[sectionId];
      if (!sectionConfig) {
        return;
      }

      const tableName = sectionConfig.tableName;
      if (!tableName) {
        return;
      }

      if (!Array.isArray(tableData[tableName])) {
        tableData[tableName] = [];
      }

      rows.forEach(rowValue => {
        const rowData: any = {};

        sectionConfig.fields.forEach(field => {
          const columnName = resolveColumnName(field);
          if (!columnName) {
            return;
          }

          if (shouldIgnoreFieldForMapping(field, columnName)) {
            return;
          }

          const columnNameLower = columnName.toLowerCase();
          if (sqlDataTypes.some(dt => columnNameLower.includes(dt) && columnNameLower.includes('('))) {
            return;
          }

          const isSystemField = ((field.inputType || field.InputType || '').toLowerCase() === 'system');

          const candidateKeys = [
            field.fieldName,
            field.UI_Field_Id,
            columnName
          ].filter(Boolean) as string[];

          let value: any = undefined;

          for (const key of candidateKeys) {
            if (key && rowValue[key] !== undefined) {
              value = rowValue[key];
              break;
            }
            if (key && rowValue[key.toLowerCase()] !== undefined) {
              value = rowValue[key.toLowerCase()];
              break;
            }
          }

          value = transformValueForSubmission(field, value);

          // CRITICAL: Don't regenerate UID for system fields if value already exists
          if ((value === undefined || value === null || value === '') && isSystemField) {
            // Special handling for UID field - don't regenerate if it exists in row data
            const isUidField = columnName && columnName.toLowerCase() === 'uid';
            if (isUidField) {
              // Check if UID exists in the input row data (case-insensitive)
              const existingUid = rowValue['uid'] || rowValue['UID'] || 
                                 rowValue[columnName] || rowValue[columnName.toLowerCase()];
              if (existingUid) {
                // UID already exists in row, use it
                value = existingUid;
              } else {
                // No UID in row, generate new one
                value = transformValueForSubmission(field, parseSystemDefaultValue(field));
              }
            } else {
              value = transformValueForSubmission(field, parseSystemDefaultValue(field));
            }
          }

          if (value === undefined) {
            const defaultValue = field.defaultValue ?? field.DefaultValue ?? field.Field_Default_Value;
            if (defaultValue !== undefined) {
              value = transformValueForSubmission(field, defaultValue);
            }
          }

          if (value === undefined) {
            value = null;
          }

          rowData[columnName] = value;
        });

        const sanitizedRow = filterRowColumns(tableName, rowData);
        (tableData[tableName] as any[]).push(sanitizedRow);
      });
    });

    const groupSystemFieldsByTable = () => {
      const grouped: Record<string, any[]> = {};

      if (!fieldConfigs || !Array.isArray(fieldConfigs)) {
        return grouped;
      }

      fieldConfigs.forEach(field => {
        const inputType = (field?.inputType || field?.InputType || '').toLowerCase();
        const tableName = field?.tableName || field?.Table_Name;

        if (inputType === 'system' && tableName) {
          if (!grouped[tableName]) {
            grouped[tableName] = [];
          }
          grouped[tableName].push(field);
        }
      });

      return grouped;
    };

    const systemFieldsByTable = groupSystemFieldsByTable();

    const getHeaderTableName = (): string | null => {
      if (preferredHeaderTableName) {
        return preferredHeaderTableName;
      }

      const headerFromConfigs = Object.keys(systemFieldsByTable).find(table => {
        const fields = systemFieldsByTable[table];
        return fields.some(f => (f?.tableType || f?.Table_Type || '').toLowerCase() === 'header');
      });

      return headerFromConfigs || null;
    };

    const ensureHeaderSystemFields = () => {
      const headerName = getHeaderTableName();
      if (!headerName) {
        return;
      }

      const headerFields = systemFieldsByTable[headerName] || [];

      if (!formData[headerName]) {
        formData[headerName] = {};
      }

      headerFields.forEach(field => {
        const columnName = resolveColumnName(field);
        if (!columnName) {
          return;
        }

        registerAllowedColumn(headerName, columnName);

        // CRITICAL: Check input form fields first (for EDIT mode)
        let valueFromInput = flatFieldValues[columnName] ?? flatFieldValues[columnName.toLowerCase()];
        
        // Ignore placeholder values - treat them as empty so they get generated
        if (typeof valueFromInput === 'string') {
          const lower = valueFromInput.toLowerCase().trim();
          if (lower === 'newid' || lower === 'currentdatetime' || lower === 'currentdate' || lower === 'backend' || lower === 'null' || lower === 'parent') {
            valueFromInput = null;
          }
        }
        
        const currentValue = formData[headerName][columnName];

        // Preserve value from input if it exists (including 0, false, empty string)
        // Only skip null/undefined
        if (valueFromInput !== undefined && valueFromInput !== null) {
          // Use value from input (EDIT mode - preserve loaded data)
          formData[headerName][columnName] = valueFromInput;
          tableData[headerName][columnName] = valueFromInput;
        } else if (currentValue === undefined || currentValue === null || currentValue === '') {
          // No value in input or current, generate default (ADD mode)
          const defaultValue = parseSystemDefaultValue(field);
          formData[headerName][columnName] = defaultValue;
          tableData[headerName][columnName] = defaultValue;
        }
      });
    };

    const ensureRepeatingSystemFields = () => {
      Object.entries(systemFieldsByTable).forEach(([tableName, fields]) => {
        if (!Array.isArray(tableData[tableName])) {
          return;
        }

        tableData[tableName].forEach((row: Record<string, any>) => {
          fields.forEach(field => {
            const columnName = resolveColumnName(field);
            if (!columnName) {
              return;
            }

            if (shouldIgnoreFieldForMapping(field, columnName)) {
              return;
            }

            registerAllowedColumn(tableName, columnName);

            const existingValue = row[columnName];

            if (existingValue === undefined || existingValue === null || existingValue === '') {
              const defaultValue = parseSystemDefaultValue(field);

              if (defaultValue !== null && defaultValue !== undefined) {
                row[columnName] = defaultValue;
              }
            }
          });
        });
      });
    };

    ensureHeaderSystemFields();
    ensureRepeatingSystemFields();

    const headerTableNameResolved = getHeaderTableName();

    // CRITICAL: Do NOT generate form number on frontend
    // The backend will generate the proper sequence number when UI_Input_Value = 'backend'
    // This matches Angular behavior where 'backend' fields return null and let the server handle it
    if (headerTableNameResolved && tableData[headerTableNameResolved]) {
      const numberFieldName = `${headerTableNameResolved}_number`;

      // Check if form number exists in input fields (EDIT mode - preserve existing number)
      let existingNumberFromInput = flatFieldValues[numberFieldName] || flatFieldValues[numberFieldName.toLowerCase()];

      // Ignore placeholder values - treat them as null so backend can generate
      if (typeof existingNumberFromInput === 'string') {
        const lower = existingNumberFromInput.toLowerCase().trim();
        if (lower === 'backend' || lower === 'null' || lower === '') {
          existingNumberFromInput = null;
        }
      }

      if (existingNumberFromInput) {
        // Preserve existing form number from input (EDIT mode)
        tableData[headerTableNameResolved][numberFieldName] = existingNumberFromInput;
      } else {
        // Set to null so backend generates the sequence number
        tableData[headerTableNameResolved][numberFieldName] = null;
        console.log(`🔢 Form number field ${numberFieldName} set to null - backend will generate`);
      }
    }

    // CRITICAL: Generate uid field for each table that has data
    let headerUID = '';
    const tablesWithData: { [tableName: string]: boolean } = {};

    // First pass: identify tables with data and get header UID
    Object.keys(tableData).forEach(tableName => {
      const data = tableData[tableName];
      const hasData = Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0;
      tablesWithData[tableName] = hasData;

      if (hasData && tableName === headerTableNameResolved) {
        // CRITICAL: Use UID from original form fields (flatFieldValues), not from tableData
        // tableData.uid may have been generated by system field processing
        let existingUidFromInput = flatFieldValues['uid'] || flatFieldValues['UID'];
        
        // Ignore placeholder values - treat them as empty
        if (typeof existingUidFromInput === 'string') {
          const lower = existingUidFromInput.toLowerCase().trim();
          if (lower === 'newid' || lower === 'backend' || lower === 'null') {
            existingUidFromInput = null;
          }
        }
        
        const existingUidFromTable = data.uid || data.UID;

        // Priority: Use UID from input form fields first, then fall back to tableData
        const existingUid = existingUidFromInput || existingUidFromTable;
        
        if (!existingUid || existingUid === '') {
          data.uid = generateUID();
        } else {
          // Preserve existing UID from input
          data.uid = existingUid;
        }
        headerUID = data.uid;
      }
    });

    const ensureHeaderReferenceUid = () => {
      if (!headerTableNameResolved || !headerUID) {
        return;
      }

      const headerRow = tableData[headerTableNameResolved];
      if (!headerRow || Array.isArray(headerRow)) {
        return;
      }

      let referenceKey = Object.keys(headerRow).find(key => key.toLowerCase() === 'reference_form_uid');

      if (!referenceKey) {
        referenceKey = 'reference_form_uid';
        registerAllowedColumn(headerTableNameResolved, referenceKey);
      }

      const currentValue = headerRow[referenceKey];
      if (currentValue === undefined || currentValue === null || currentValue === '' || (typeof currentValue === 'string' && currentValue.toLowerCase() === 'backend')) {
        headerRow[referenceKey] = headerUID;
      }
    };

    ensureHeaderReferenceUid();

    // Second pass: process all tables with proper structure
    Object.keys(tableData).forEach(tableName => {
      const data = tableData[tableName];

      // Only process tables that have actual field data
      if (tablesWithData[tableName]) {
        const isHeaderTable = tableName === headerTableNameResolved;

        if (isHeaderTable) {
          // Header tables are single objects (already sanitized earlier)
          formData[tableName] = filterRowColumns(tableName, data);
        } else {
          // CRITICAL: Only send detail tables if they have actual user-filled data
          // For customer portal, detail tables are usually empty, so don't send them
          const dataKeys = Array.isArray(data) ? data : [data];
          const hasActualData = dataKeys.some(entry => Object.keys(entry).some(key =>
            key !== 'uid' &&
            key !== 'reference_form_uid' &&
            entry[key] !== null &&
            entry[key] !== undefined &&
            entry[key] !== ''
          ));

          if (hasActualData) {
            const rowsToProcess = Array.isArray(data) ? data : [data];
            const processedRows = rowsToProcess.map(row => {
              const rowCopy = filterRowColumns(tableName, { ...row });
              if (!rowCopy.uid || rowCopy.uid === '' || String(rowCopy.uid).toLowerCase() === 'newid') {
                rowCopy.uid = generateUID();
              }

              const parentField = sectionConfigParentField(tableName, systemFieldsByTable);
              if (parentField && headerUID) {
                rowCopy[parentField] = headerUID;
              }

              if (headerUID && !rowCopy.reference_form_uid) {
                rowCopy.reference_form_uid = headerUID;
              }

              return rowCopy;
            });

            formData[tableName] = processedRows;
          }
        }
      }
    });

    // CRITICAL: Set default values for specific system fields (matching Angular behavior)
    // These fields support sub-ticket functionality and form referencing
    // If they exist in the field configuration, ensure they have default values
    const systemFieldDefaults: { [fieldName: string]: any } = {
      'sub_ticket_count': 0,
      'is_sub_ticket': 0,
      'open_sub_ticket_uid': 0,
      'open_sub_ticket_count': 0,
      'reference_form_number': 0,
      'is_billing_processed': 0,
      'reference_form_name': 0,
      'reference_form_uid': 0
    };

    // Apply defaults to header tables
    Object.keys(formData).forEach(tableName => {
      const tableDataObj = formData[tableName];

      // For header tables (single object)
      if (!Array.isArray(tableDataObj)) {
        Object.keys(systemFieldDefaults).forEach(fieldName => {
          // Check if this field exists in the field configuration for this table
          const fieldExistsInConfig = fieldConfigs?.some(field => {
            const columnName = (field.Table_Column_Name || field.tableColumnName || field.columnName || '').toLowerCase();
            const fieldTable = (field.Table_Name || field.tableName || '').toLowerCase();
            return columnName === fieldName.toLowerCase() && fieldTable === tableName.toLowerCase();
          });

          // If field exists in config and doesn't have a value (or is null/undefined), set default
          if (fieldExistsInConfig) {
            if (tableDataObj[fieldName] === undefined || tableDataObj[fieldName] === null || tableDataObj[fieldName] === '') {
              tableDataObj[fieldName] = systemFieldDefaults[fieldName];
            }
          }
        });
      }
    });

    return formData;
  }

  /**
   * Complete form submission workflow
   * 1. Upload images if present
   * 2. Prepare form data
   * 3. Submit to Web API
   */
  async submitFormWithImages(
    formName: string,
    formType: string,
    formFields: any,
    imageFiles?: { [fieldName: string]: File[] },
    options?: {
      nextStatus?: string;
      approvalMatrixLineUID?: string;
      isReverted?: boolean;
      selectedRevertHistoryUID?: string;
      remarks?: string;
      tableNames?: { header?: string; detail?: string };
      mode?: 'ADD' | 'EDIT' | 'VIEW';
      currentStatus?: string;
      uid?: string;
      isPublicForm?: boolean;
    }
  ) {
    try {
      let imageBase64Data: { [fieldName: string]: string[] } = {};

      // Step 1: Convert images to base64 if present
      if (imageFiles && Object.keys(imageFiles).length > 0) {
        for (const [fieldName, files] of Object.entries(imageFiles)) {
          if (files && files.length > 0) {
            const base64Promises = files.map(file => this.convertFileToBase64(file));
            const base64Results = await Promise.all(base64Promises);
            imageBase64Data[fieldName] = base64Results;
          }
        }
      }

      // Step 2: Get form configuration to map field names to column names
      const configResponse = await this.getFormConfiguration(
        formName,
        options?.mode || 'ADD',
        options?.currentStatus || ''
      );
      const fieldConfigs = configResponse.success ? configResponse.data : [];

      // Step 3: Prepare form data with base64 images
      const formData = this.prepareFormData(formType, formFields, imageBase64Data, options?.tableNames, fieldConfigs);

      const nextStatusFromConfig = configResponse.success && configResponse.nextStatus
        ? String(configResponse.nextStatus).trim()
        : '';

      const callerNextStatus = options?.nextStatus ? String(options.nextStatus).trim() : '';
      const effectiveNextStatus = callerNextStatus || nextStatusFromConfig;


      const applyNextStatusToPayload = (data: any, nextStatus?: string) => {
        if (!nextStatus) {
          return;
        }

        const applyToRow = (row: Record<string, any>) => {
          if (!row || typeof row !== 'object') {
            return;
          }

          const keys = Object.keys(row);
          const directMatch = keys.find(key => key.toLowerCase() === 'status');
          const derivedMatch = directMatch || keys.find(key => key.toLowerCase().endsWith('_status'));

          if (derivedMatch) {
            row[derivedMatch] = nextStatus;
          }
        };

        Object.keys(data || {}).forEach(tableName => {
          const tableValue = data[tableName];
          if (!tableValue) {
            return;
          }

          if (Array.isArray(tableValue)) {
            tableValue.forEach(applyToRow);
          } else if (typeof tableValue === 'object') {
            applyToRow(tableValue);
          }
        });
      };

      applyNextStatusToPayload(formData, effectiveNextStatus);

      // Step 4: Submit to Web API (logging happens inside submitDynamicForm)
      const result = await this.submitDynamicForm(formName, formData, {
        nextStatus: effectiveNextStatus,
        approvalMatrixLineUID: options?.approvalMatrixLineUID,
        isReverted: options?.isReverted,
        selectedRevertHistoryUID: options?.selectedRevertHistoryUID,
        isPublicForm: options?.isPublicForm,
        remarks: options?.remarks,
        uid: options?.uid || (formFields as any).uid,  // UID from options or form data
        mode: options?.mode
      });

      return result;
    } catch (error: any) {
      console.error('Error in form submission workflow:', error);
      return {
        success: false,
        message: error.message || 'Failed to submit form',
        errors: []
      };
    }
  }

  /**
   * Get form configuration using GetFilteredApprovalMatrixData
   * This is exactly how the main UI does it
   */
  async getFormConfiguration(formName: string, mode: string = 'ADD', currentStatus: string = '') {
    try {
      // Get OrgTypeUID and RoleTypeUID from localStorage (stored during auth)
      // For registration/verification forms (no login), use default External Customer values
      const orgTypeUID = localStorage.getItem('orgTypeUID') || 'BU';
      const roleTypeUID = localStorage.getItem('roleTypeUID') || '0dcae222-5f10-4452-a15a-50280be4c0ce';

      // Use the stored OrgTypeUID and RoleTypeUID from auth response
      const params = new URLSearchParams({
        FormName: formName,
        OrgTypeUID: orgTypeUID,
        RoleTypeUID: roleTypeUID,
        CurrentStatus: currentStatus,  // Pass current status for EDIT mode
        Mode: mode
      });

      // Call the Web API directly
      const response = await this.webApiClient.get(`/ApprovalMatrix/GetFilteredApprovalMatrixData?${params.toString()}`);

      // Check if response has data - same structure as main UI
      if (response.data?.Status && response.data?.Data) {
        const filteredData = response.data.Data.FilteredApprovalMatrixData;

        if (!filteredData || !filteredData.ApprovalMatrixLineFormConfigurations) {
          return {
            success: false,
            data: [],
            tableNames: {},
            fieldsByTable: {},
            approvalMatrixLines: [],
            message: 'No form configuration available'
          };
        }

        // Extract configuration exactly like main UI does
        const configData = filteredData.ApprovalMatrixLineFormConfigurations || [];
        const approvalMatrixLineUID = filteredData.ApprovalMatrixLineUID || '';

        // console.log('Extracted approvalMatrixLineUID:', approvalMatrixLineUID);
        // console.log('FilteredData keys:', Object.keys(filteredData));

        // Extract table names from the fields
        const tableNames: { header?: string; detail?: string } = {};
        const fieldsByTable: { [tableName: string]: any[] } = {};

        // Process fields using the same format as GetFilteredApprovalMatrixData response
        const processedFields = configData.map((field: any) => {
          const tableName = field.Table_Name || field.TableName;
          const tableType = field.Table_Type || field.TableType;
          const columnName = field.Table_Column_Name || field.TableColumnName;

          // Store table names
          if (tableType === 'header' && !tableNames.header) {
            tableNames.header = tableName;
          } else if (tableType === 'detail' && !tableNames.detail) {
            tableNames.detail = tableName;
          }

          // Group fields by table
          if (!fieldsByTable[tableName]) {
            fieldsByTable[tableName] = [];
          }

          // Map to the format expected by DynamicServiceRequest
          const processedField = {
            id: field.Id || field.id,
            uid: field.Uid || field.UID,
            formName: field.Form_Name || field.FormName,
            tableName: tableName,
            tableType: tableType,
            columnName: columnName,
            columnType: field.Table_Column_Type || field.TableColumnType,
            columnSize: field.Table_Column_Size || field.TableColumnSize,
            mainTab: field.UI_Main_Tab || field.UiMainTab || 'General',
            subTab: field.UI_Sub_Tab || field.UiSubTab,
            fieldId: field.UI_Field_Id || field.UiFieldId,
            fieldText: field.UI_Field_Text || field.UiFieldText,
            inputType: field.UI_Input_Type || field.UiInputType,
            inputValue: field.UI_Input_Value || field.UiInputValue,
            isVisible: field.UI_Field_IsVisible !== undefined ? field.UI_Field_IsVisible : (field.UiFieldIsVisible !== false),
            isEnabled: field.UI_Field_IsEnabled !== undefined ? field.UI_Field_IsEnabled : (field.UiFieldIsEnabled !== false),
            isMandatory: field.UI_Field_IsMandatory !== undefined ? field.UI_Field_IsMandatory : field.UiFieldIsMandatory,
            parentFieldId: field.UI_Parent_Field_Id || field.UiParentFieldId,
            parentFieldValue: field.UI_Parent_Field_Value || field.UiParentFieldValue,
            conditionOperator: field.Condition_Operator || field.ConditionOperator || 'equals',
            conditionalLogicOperator: field.Conditional_Logic_Operator || field.ConditionalLogicOperator,
            secondaryParentFieldId: field.Secondary_Parent_Field_Id || field.SecondaryParentFieldId,
            secondaryParentFieldValue: field.Secondary_Parent_Field_Value || field.SecondaryParentFieldValue,
            
            // Repeating Section Configuration
            isRepeatingSection: field.Is_Repeating_Section || field.IsRepeatingSection || false,
            repeatingSectionId: field.Repeating_Section_Id || field.RepeatingSectionId || '',
            repeatingSectionName: field.Repeating_Section_Name || field.RepeatingSectionName || '',
            repeatingTriggerType: field.Repeating_Trigger_Type || field.RepeatingTriggerType || '',
            repeatingTriggerFieldId: field.Repeating_Trigger_Field_Id || field.RepeatingTriggerFieldId || '',
            repeatingSectionApiEndpoint: field.Repeating_Section_api_EndPoint || field.RepeatingSectionApiEndpoint || '',
            repeatingMinRows: field.Repeating_Min_Rows || field.RepeatingMinRows || 0,
            repeatingMaxRows: field.Repeating_Max_Rows || field.RepeatingMaxRows || 100,
            repeatingAllowAddRemove: field.Repeating_Allow_Add_Remove !== undefined ? field.Repeating_Allow_Add_Remove : (field.RepeatingAllowAddRemove !== false),
            repeatingLayout: field.Repeating_Layout || field.RepeatingLayout || 'table',
            repeatingShowRowNumbers: field.Repeating_Show_Row_Numbers !== undefined ? field.Repeating_Show_Row_Numbers : (field.RepeatingShowRowNumbers !== false),
            repeatingShowDeleteButton: field.Repeating_Show_Delete_Button !== undefined ? field.Repeating_Show_Delete_Button : (field.RepeatingShowDeleteButton !== false),
            repeatingRowTitleTemplate: field.Repeating_Row_Title_Template || field.RepeatingRowTitleTemplate || '{index}',
            repeatingParentSectionId: field.Repeating_Parent_Section_Id || field.RepeatingParentSectionId || '',
            repeatingChildSectionIds: field.Repeating_Child_Section_Ids ? 
              (typeof field.Repeating_Child_Section_Ids === 'string' ? field.Repeating_Child_Section_Ids.split(',').map((s: string) => s.trim()) : field.Repeating_Child_Section_Ids) : [],
            repeatingNestedLevel: field.Repeating_Nested_Level || field.RepeatingNestedLevel || 0,
            repeatingAllowNesting: field.Repeating_Allow_Nesting !== undefined ? field.Repeating_Allow_Nesting : (field.RepeatingAllowNesting !== false),
            
            optionsSource: field.Field_Options_Source || field.FieldOptionsSource,
            staticOptions: field.Field_Options_Static_Values || field.FieldOptionsStaticValues,
            apiEndpoint: field.Field_Options_API_Endpoint || field.FieldOptionsApiEndpoint,
            validationRules: field.Field_Validation_Rules || field.FieldValidationRules,
            cssClasses: field.Field_CSS_Classes || field.FieldCssClasses,
            helpText: field.Field_Help_Text || field.FieldHelpText,
            mainTabOrder: field.UI_Main_Tab_Order || field.UiMainTabOrder,
            subTabOrder: field.UI_Sub_Tab_Order || field.UiSubTabOrder,
            fieldOrder: field.UI_Field_Order || field.UiFieldOrder,

            // TextBoxWithSearch API endpoints (up to 5)
            onChangeApiEndpoint1: field.On_ChangeAPI_EndPoint_1 || field.On_ChangeAPI_Endpoint_1 || field.OnChangeApiEndPoint1,
            onChangeApiEndpoint2: field.On_ChangeAPI_EndPoint_2 || field.On_ChangeAPI_Endpoint_2 || field.OnChangeApiEndPoint2,
            onChangeApiEndpoint3: field.On_ChangeAPI_EndPoint_3 || field.On_ChangeAPI_Endpoint_3 || field.OnChangeApiEndPoint3,
            onChangeApiEndpoint4: field.On_ChangeAPI_EndPoint_4 || field.On_ChangeAPI_Endpoint_4 || field.OnChangeApiEndPoint4,
            onChangeApiEndpoint5: field.On_ChangeAPI_EndPoint_5 || field.On_ChangeAPI_Endpoint_5 || field.OnChangeApiEndPoint5,
            onChangeApiCount: field.On_Change_API_Count || field.OnChangeApiCount || 1,
            isOnChangeFetchDataFromAPI: field.Is_OnChange_FetchData_From_API === true || field.Is_OnChange_FetchData_From_API === 1,

            // Auto-population configuration
            isApiPopulatedColumn: field.Is_API_Populated_Column === true || field.Is_API_Populated_Column === 1,
            apiPopulatedColumnParentFieldId: field.API_Populated_Column_Parent_Field_Id || field.ApiPopulatedColumnParentFieldId,
            apiPopulatedColumnParentFieldIdPropertyName: field.API_Populated_Column_Parent_Field_Id_PropertyName || field.ApiPopulatedColumnParentFieldIdPropertyName,
            apiNumber: field.API_Number || field.ApiNumber || 1
          };

          fieldsByTable[tableName].push(processedField);
          return processedField;
        });


        // Use fallback if NextStatus is empty - typically "Draft" for new forms
        const nextStatus = filteredData.NextStatus || 'Draft';
        
        if (!filteredData.NextStatus) {
          console.warn('⚠️ NextStatus is empty, using fallback:', nextStatus);
        }

        return {
          success: true,
          data: processedFields,
          tableNames,
          fieldsByTable,
          approvalMatrixLineUID,
          canCreate: filteredData.CanCreate,
          canEdit: filteredData.CanEdit,
          canApprove: filteredData.CanApprove,
          canReject: filteredData.CanReject,
          canRevert: filteredData.CanRevert,
          nextStatus: nextStatus,
          nextUserField: filteredData.NextUserField,
          message: 'Configuration loaded'
        };
      }

      return {
        success: false,
        data: [],
        tableNames: {},
        fieldsByTable: {},
        approvalMatrixLines: [],
        message: 'Failed to load configuration'
      };
    } catch (error: any) {
      console.error('Error fetching form configuration:', error);

      // If the endpoint doesn't exist or fails, return empty config
      // The form can still work with default table names
      return {
        success: false,
        data: [],
        tableNames: {},
        fieldsByTable: {},
        approvalMatrixLines: [],
        message: error.response?.status === 404
          ? 'Form configuration endpoint not found'
          : 'Failed to fetch form configuration'
      };
    }
  }

  /**
   * Get dropdown options from Web API
   */
  async getDropdownOptions(entityName: string, fieldName: string) {
    try {
      const response = await this.webApiClient.get(`/FormConfiguration/GetDropdownOptions`, {
        params: {
          entityName,
          fieldName
        }
      });

      if (response.data?.Success || response.data?.success) {
        return {
          success: true,
          options: response.data.Options || response.data.options || [],
          apiEndpoint: response.data.ApiEndpoint
        };
      }

      return {
        success: false,
        options: [],
        message: response.data?.Message || 'Failed to load options'
      };
    } catch (error: any) {
      console.error('Error fetching dropdown options:', error);
      return {
        success: false,
        options: [],
        message: error.message || 'Failed to fetch options'
      };
    }
  }

  /**
   * Bind equipment data by serial number
   * Calls SP_BINDDROPDOWN_GENERICDATA to get equipment details
   */
  async bindEquipmentBySerialNumber(
    serialNumber: string,
    moduleName: string = 'Equipment',
    formUID?: string
  ): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      if (!serialNumber?.trim()) {
        return {
          success: false,
          message: 'Serial number is required'
        };
      }


      // Build query parameters for GetListDataByModule API - same as portal
      const params = new URLSearchParams({
        ModuleName: 'EquipmentMaster-SerialNumber',
        SerialNumber: serialNumber.trim()
      });

      // Add FormUID if provided
      if (formUID) {
        params.append('FormUID', formUID);
      }

      // Call the Web API directly
      const response = await this.webApiClient.get(`/Dropdown/GetListDataByModule?${params.toString()}`);


      // Check Web API response format - GetListDataByModule returns list data
      if (response.data?.Status?.Type === 1 && response.data?.Data) {
        // GetListDataByModule returns data in ModelData array
        const equipmentData = response.data.Data.ModelData || response.data.Data;


        return {
          success: true,
          data: equipmentData,
          message: 'Equipment data retrieved successfully'
        };
      } else {
        return {
          success: false,
          message: response.data?.Status?.Msg || 'No equipment data found for this serial number'
        };
      }
    } catch (error: any) {
      console.error('Error binding equipment by serial number:', error);
      return {
        success: false,
        message: error.response?.data?.Status?.Msg || error.message || 'Failed to bind equipment data'
      };
    }
  }

  /**
   * Get form grid configuration (tabs, filters, procedure name)
   */
  async getFormGridConfiguration(formName: string) {
    try {
      const response = await this.webApiClient.post(
        `/FormsGrid/GetFormGridConfiguration?FormName=${encodeURIComponent(formName)}`,
        null
      );

      // Check for success - Status can be a string 'Success' or an object with Code: 200
      const isSuccess = response.data?.Status === 'Success' || 
                       response.data?.Status?.Code === 200 ||
                       response.data?.Status?.Type === 1;

      if (isSuccess && response.data?.Data) {
        return {
          success: true,
          data: response.data.Data
        };
      }

      console.error('❌ Grid configuration API failed:', {
        status: response.data?.Status,
        message: response.data?.Message,
        data: response.data
      });

      return {
        success: false,
        data: null,
        message: response.data?.Message || 'Failed to load grid configuration'
      };
    } catch (error: any) {
      console.error('❌ Error fetching grid configuration:', error);
      console.error('❌ Error details:', error.response?.data);
      return {
        success: false,
        data: null,
        message: error.message || 'Failed to load grid configuration'
      };
    }
  }

  /**
   * Bind grid data using stored procedure
   */
  async bindGridData(request: any) {
    try {
      const response = await this.webApiClient.post(
        '/FormsGrid/BindGridData',
        request
      );

      // Check for success - Status can be a string 'Success' or an object with Code: 200
      const isSuccess = response.data?.Status === 'Success' || 
                       response.data?.Status?.Code === 200 ||
                       response.data?.Status?.Type === 1;

      if (isSuccess && response.data?.Data) {
        return {
          success: true,
          data: response.data.Data
        };
      }

      console.error('❌ BindGridData API failed:', response.data);
      return {
        success: false,
        data: null,
        message: response.data?.Message || response.data?.Status?.Msg || 'Failed to load grid data'
      };
    } catch (error: any) {
      console.error('❌ Error fetching grid data:', error);
      console.error('❌ Error details:', error.response?.data);
      return {
        success: false,
        data: null,
        message: error.message || 'Failed to load grid data'
      };
    }
  }
}

// Form type mappings
export const FORM_TYPES = {
  CDE_SERVICE: 'CDE_SERVICE',
  PULLOUT_REQUEST: 'PULLOUT_REQUEST',
  WATER_SAMPLING: 'WATER_SAMPLING',
  RELOCATION: 'RELOCATION',
  COMPLAINT: 'COMPLAINT',
  REGISTRATION: 'REGISTRATION'
};

// Form names as they appear in the database
export const FORM_NAMES = {
  CDE_SERVICE: 'CDE Service Call',
  PULLOUT_REQUEST: 'CDE Removal',
  WATER_SAMPLING: 'Water Sampling',
  RELOCATION: 'CDE Relocation',
  COMPLAINT: 'Complaint',
  REGISTRATION: 'CDE Registration',
  VERIFICATION: 'CDE Verification using Serial Number',
  CUSTOMER_UNIT_REGISTRATION: 'Customer Unit Registration'
};

// Export singleton instance
const webApiFormService = new WebApiFormService();
export default webApiFormService;

// Export class for testing
export { WebApiFormService };